<template>
  <div>
    <b-alert v-if="!konfiguration" show variant="warning">
      <TriangleAlert :size="16" class="text-white" />
      Keine gültige Konfiguration gefunden!
    </b-alert>
    <div class="d-flex justify-content-end align-items-center">
      <b-button
        v-if="vorgaenge && vorgaenge.length > 0"
        class="icon-button flex-shrink-0 mb-4"
        :title="areAllVorgaengeOpen ? 'Alles einklappen' : 'Alles ausklappen'"
        v-b-tooltip.hover
        @click="toggleAllVorgaenge"
      >
        <Maximize2 v-if="!areAllVorgaengeOpen" :size="20" />
        <Minimize2 v-else :size="20" />
      </b-button>
    </div>

    <b-skeleton-table v-if="isLoading" :rows="5" :columns="4"></b-skeleton-table>
    <template v-else>
      <div v-if="!vorgaenge || vorgaenge.length === 0" class="text-muted">Keine Vorgänge vorhanden</div>
      <TeilnehmerVorgangCard
        v-for="vorgang in vorgaenge"
        :key="vorgang.id"
        :vorgang="vorgang"
        :konfiguration="konfiguration"
        :reiseterminStartdatum="reiseterminStartdatum"
        :reiseterminEnddatum="reiseterminEnddatum"
        :isReiseOhneFlug="isReiseOhneFlug"
        :isEditAllowed="isEditAllowed"
        :dokumenteByTeilnehmer="dokumenteByVorgang[vorgang.id] || {}"
        :saveHandler="(teilnehmer, changes) => saveTeilnehmerFelder(vorgang, teilnehmer, changes)"
        :expanded="!!vorgangOpenState[vorgang.id]"
        :isReiseterminInReleasePruefung="isReiseterminInReleasePruefung"
        :passdatenCheckRequired="passdatenCheckRequired"
        :dokumenteCheckRequired="dokumenteCheckRequired"
        :reiseterminState="reiseterminObj && reiseterminObj.state"
        @update:expanded="value => $set(vorgangOpenState, vorgang.id, value)"
        @geprueft-changed="value => onGeprueftChanged(vorgang, value)"
        @dokument-uploaded="onDokumentUploaded"
        @dokument-deleted="onDokumentDeleted"
      ></TeilnehmerVorgangCard>
    </template>
  </div>
</template>

<script>
import TeilnehmerVorgangCard from './release-teilnehmerdaten/teilnehmer-vorgang-card.vue';
import {
  REFRESH_VORGANG_RELEASEPROZESSE,
  UPDATE_VORGANG_RELEASEPROZESS,
} from '../../../core/vorgaenge/stores/vorgaenge.module';
import { updateTeilnehmer, groupDokumenteByVorgang } from '@/core/vorgaenge/teilnehmer-passdaten.service';
import { hasTeilnehmerdatenVollzugriff } from '@/core/vorgaenge/helpers/teilnehmer-passdaten.utils';
import { isReiseOhneFlug } from '../../../core/common/helpers/utils';
import { Maximize2, Minimize2, TriangleAlert } from 'lucide-vue';

export default {
  components: { TeilnehmerVorgangCard, Maximize2, Minimize2, TriangleAlert },
  props: {
    isLoading: { type: Boolean },
    reiseterminStartdatum: { type: [String, Object] },
    reiseterminEnddatum: { type: [String, Object] },
    vorgaenge: { type: Array },
    konfiguration: { type: Object },
    reiseterminObj: { type: Object },
    isReiseterminInReleasePruefung: { type: Boolean },
    passdatenCheckRequired: { type: Boolean },
    dokumenteCheckRequired: { type: Boolean },
  },
  data() {
    return {
      vorgangOpenState: {},
    };
  },
  computed: {
    dokumenteByVorgang() {
      return groupDokumenteByVorgang(this.vorgaenge);
    },
    areAllVorgaengeOpen() {
      if (!this.vorgaenge || this.vorgaenge.length === 0) return false;
      return this.vorgaenge.every(vorgang => this.vorgangOpenState[vorgang.id] === true);
    },
    isReiseOhneFlug() {
      return isReiseOhneFlug(this.reiseterminObj?.metadata);
    },
    isEditAllowed() {
      return hasTeilnehmerdatenVollzugriff(this.$store);
    },
  },
  methods: {
    toggleAllVorgaenge() {
      const shouldOpen = !this.areAllVorgaengeOpen;
      (this.vorgaenge || []).forEach(vorgang => {
        this.$set(this.vorgangOpenState, vorgang.id, shouldOpen);
      });
    },
    // Die Checkbox setzt Passdaten- und Dokumentenprüfung gemeinsam
    onGeprueftChanged(vorgang, istGeprueft) {
      const wert = istGeprueft ? 'GeprueftVorhandenKorrekt' : 'NichtGeprueft';
      this.$store
        .dispatch(UPDATE_VORGANG_RELEASEPROZESS, [
          {
            vorgangId: vorgang.id,
            gebuchteReiseleistungListenversandGeprueft: vorgang.gebuchteReiseleistungListenversandGeprueft,
            passdatenGeprueft: wert,
            dokumenteGeprueft: wert,
          },
        ])
        .then(() => {
          this.toast(this.$t('WORKFLOWS.COMMON.SAVED'));
          this.$emit('workflow-updated');
        })
        .catch(error => {
          console.error('Fehler beim Speichern der Prüfung:', error);
          this.toast('Fehler beim Speichern der Prüfung', { variant: 'danger' });
        });
    },
    onDokumentUploaded({ vorgangId, teilnehmerNummer, dokumente }) {
      const teilnehmer = this.findTeilnehmer(vorgangId, teilnehmerNummer);
      if (teilnehmer) {
        this.$set(teilnehmer, 'dokumente', dokumente);
      }
      this.refreshReleaseprozessStatus();
    },
    onDokumentDeleted(dokument) {
      const teilnehmer = this.findTeilnehmer(dokument.vorgangId, dokument.teilnehmerNummer);
      if (teilnehmer) {
        this.$set(
          teilnehmer,
          'dokumente',
          (teilnehmer.dokumente || []).filter(d => d.id !== dokument.id)
        );
      }
      this.refreshReleaseprozessStatus();
    },
    // Sucht einen Teilnehmer anhand von Vorgangs-Id und Teilnehmernummer in den geladenen Vorgängen
    findTeilnehmer(vorgangId, teilnehmerNummer) {
      const vorgang = (this.vorgaenge || []).find(v => v.id === vorgangId);
      return (vorgang?.teilnehmer || []).find(t => t.nummer === teilnehmerNummer) || null;
    },

    // Speichert alle geänderten Felder eines Teilnehmers in einem Aufruf
    async saveTeilnehmerFelder(vorgang, teilnehmer, changes) {
      try {
        await updateTeilnehmer(vorgang.id, teilnehmer.nummer, changes);
        Object.entries(changes).forEach(([key, value]) => this.$set(teilnehmer, key, value));
        this.toast('Gespeichert');
        this.refreshReleaseprozessStatus();
      } catch (error) {
        console.error('Fehler beim Speichern der Teilnehmerdaten:', error);
        this.toast('Fehler beim Speichern der Teilnehmerdaten', { variant: 'danger' });
        throw error;
      }
    },
    refreshReleaseprozessStatus() {
      if (!this.reiseterminObj?.id) return;
      this.$store
        .dispatch(REFRESH_VORGANG_RELEASEPROZESSE, { reiseterminId: this.reiseterminObj.id })
        .then(() => {
          this.$emit('workflow-updated');
        });
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.passdaten-col) {
  width: 50rem;
}
:deep(.th-no-padding-top > th) {
  padding-top: 0px;
}
:deep(.th-no-padding-top > th > div) {
  color: var(--text-muted);
}
:deep(.cw-5) {
  width: 5%;
}
:deep(.cw-10) {
  width: 10%;
}
:deep(.cw-30) {
  width: 30%;
}
:deep(.cw-60) {
  width: 60%;
}
</style>
